import { Link, useNavigate } from 'react-router-dom';
import useQueryValues from '@/hooks/useQueryValues';
import usePaginationQuery from '@/hooks/usePaginationQuery';
import { useEffect, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { useQuery } from '@tanstack/react-query';
import MainContentTitle from '@/components/molecule/MainContentTitle';
import { NAV_CONST } from '@/src/constants/nav';
import Filter from '@/components/organism/Filter';
import SectionCardNoneTitle from '@/components/molecule/SectionCardNoneTitle';
import Typo from '@/components/atom/Typo';
import PATH from '@/src/routes/path';
import Button from '@/components/atom/Button';
import LucideIcon from '@/components/atom/Icons/LucideIcon';
import Table from '@/components/atom/Table';
import {
  getRecyclingProductsAPI,
  getRecyclingProductsExcelAPI,
} from '@/src/apis/rest/recyclingProducts';
import { recyclingProductFilters } from '@/src/pages/RecyclingProducts/filter';
import { recyclingProductListColumn } from '@/src/pages/RecyclingProducts/column';
import ExcelDownloadButton from '@/src/components/atom/ExcelDownloadButton';

function RecyclingProductsListPage() {
  const navigate = useNavigate();

  const { defaultValues: defaultSearchParams } = useQueryValues({
    createdAt: ['createdAtFrom', 'createdAtTo'],
  });
  const { page, pageSize, handlePageChange } = usePaginationQuery();

  const gridRef = useRef<AgGridReact>(null);
  const [searchParams, setSearchParams] = useState(defaultSearchParams);

  const {
    data: recyclingProductsListData,
    isError,
    isFetching,
  } = useQuery({
    queryKey: ['recycling-product', searchParams, page, pageSize],
    queryFn: () => {
      return getRecyclingProductsAPI({
        ...searchParams,
        page,
        pageSize,
      });
    },
  });

  useEffect(() => {
    if (isFetching && gridRef.current) {
      gridRef.current.api?.showLoadingOverlay();
    }
  }, [isFetching]);

  return (
    <>
      <MainContentTitle
        pageTitle={NAV_CONST.recyclingProductManagement}
        breadcrumbList={[NAV_CONST.recyclingProductManagement]}
      />
      <div className="mb-20 flex flex-col gap-6">
        <Filter
          title="검색 필터"
          filters={recyclingProductFilters()}
          onSubmit={values => {
            handlePageChange(1, pageSize, false);
            setSearchParams(values);
          }}
        />
        <div className="flex flex-col gap-y-6">
          <SectionCardNoneTitle
            contentProps={{
              className: 'flex flex-col gap-y-4',
            }}
          >
            <div className="flex items-center justify-between">
              <Typo typoType="b18m">수거 품목 목록</Typo>
              <div className="flex gap-[8px]">
                <ExcelDownloadButton
                  disabled={
                    isError ||
                    isFetching ||
                    !recyclingProductsListData?.rows?.length
                  }
                  fileName="수거_품목_리스트"
                  downloadExcelAPI={getRecyclingProductsExcelAPI}
                  searchParams={searchParams}
                />
                <Link to={PATH.RECYCLING_PRODUCT_CREATE}>
                  <Button buttonSize="medium" onClick={() => {}}>
                    <div className="flex items-center gap-x-2">
                      <LucideIcon name="Plus" color="white" />
                      수거 품목 등록
                    </div>
                  </Button>
                </Link>
              </div>
            </div>

            <Table
              ref={gridRef}
              rowData={isError ? [] : recyclingProductsListData?.rows || []}
              isPaginationDataMapping
              columnDefs={recyclingProductListColumn(e => {
                navigate(
                  PATH.RECYCLING_PRODUCT_DETAIL.replace(':id', String(e)),
                );
              })}
              total={recyclingProductsListData?.count || 0}
              page={page}
              pageSize={pageSize}
              handlePaginationClick={handlePageChange}
            />
          </SectionCardNoneTitle>
        </div>
      </div>
    </>
  );
}

export default RecyclingProductsListPage;
